<template>
  <!-- Design: job panel → "Weitere Stellen des Unternehmens" -->
  <div class="p-4 p-lg-5">
    <h2 class="mb-3">{{ title }}</h2>
    <ts-results-pagination v-if="count > perPage" id-prefix="tsCompanyJobsTop" :page.sync="page" :per-page.sync="perPage" :total="count" />
    <div class="ts-results-list position-relative d-flex flex-column gap-2 my-3" :class="{ 'ts-results-list-loading': loading && !jobs.length }">
      <div v-if="loading" class="ts-loading d-flex align-items-center justify-content-center" role="status">
        <span class="spinner-border" aria-hidden="true"></span>
        <span class="visually-hidden">{{ loadingText }}</span>
      </div>
      <ts-job-item
        v-for="item in jobs"
        :id="item[1]"
        :key="item[1]"
        :date="item[0]"
        :title="item[2]"
        :location="item[3]"
        :firm-name="item[4]"
        :shortview="item[7]"
        :contact="item[8]"
        :active="isInNotepad(item[1])"
        :is-auth="isAuth"
        in-panel
        @opened="$emit('opened')"
      />
    </div>
    <ts-results-pagination v-if="count > perPage" id-prefix="tsCompanyJobsBottom" :page.sync="page" :per-page.sync="perPage" :total="count" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import { CompanyModule } from '@/store/modules/company';
import { getCompanySearch } from '@/api/Company';
import { isJobInNotepad } from '@/sections/jobs/notepad';
import { JobType } from '@/types/ProfessionType';
import TsResultsPagination from '@/components/Talentspace/results/TsResultsPagination.vue';
import TsJobItem from '@/components/Talentspace/results/TsJobItem.vue';

// The other jobs of the ad's company: the company is found by its name (as before), then its
// list is loaded page by page. A job opened here replaces the ad in the panel ('opened').
@Component({
  components: { TsResultsPagination, TsJobItem },
})
export default class TsJobCompanyJobs extends Vue {
  @Prop({ default: '' }) readonly company!: string;

  loading = false;

  get title() {
    return `${AppModule.translation[16568]} ${this.company}`;
  }

  get loadingText() {
    return AppModule.translation[16555];
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  get jobs(): any[] {
    return CompanyModule.companyJobs.jobs || [];
  }

  get count(): number {
    return CompanyModule.companyJobs.amt || 0;
  }

  get page(): number {
    return Number(CompanyModule.companyJobPagination.page) || 1;
  }

  set page(value: number) {
    CompanyModule.SetPaginationCompany({ obj: 'page', value });
    this.loadJobs();
  }

  get perPage(): number {
    return Number(CompanyModule.companyJobPagination.per_page) || 20;
  }

  set perPage(value: number) {
    CompanyModule.SetPaginationCompany({ obj: 'per_page', value });
    CompanyModule.SetPaginationCompany({ obj: 'page', value: 1 });
    this.loadJobs();
  }

  isInNotepad(id: number) {
    return isJobInNotepad(id);
  }

  async loadJobs() {
    this.loading = true;
    try {
      // Companies of an occupational field list only the jobs of that field
      const params = CompanyModule.companyType === JobType.cluster
        ? { sid: AppModule.languageSid, cluster_id: CompanyModule.companyClusterParams.cluster_id }
        : { sid: AppModule.languageSid };
      await CompanyModule.GetCompanyJobsSearch(params);
    } finally {
      this.loading = false;
    }
  }

  async findCompany() {
    const found = await getCompanySearch({ sid: AppModule.languageSid, name: this.company, radius: 0 });
    if (found.length) {
      await CompanyModule.SetCompanyDetailFilter({ obj: 'idcomp', value: Number(found[0].id) });
    }
  }

  async created() {
    this.loading = true;
    await CompanyModule.SetDefaultCompanyJobsItems();
    if (this.company) await this.findCompany();
    await this.loadJobs();
  }
}
</script>
